<template>
    <div>
        <!-- <Loader :field="'companies'" v-if="loadingScoutedJobs || listLoading" /> -->
        <div class="job-list">
            <!-- Scouted Jobs Section -->
            <Loader :field="'scouted-jobs'" v-if="loadingScoutedJobs" />
            <div v-else class="job-section">
                <div class="scouted-header d-flex justify-content-between align-items-center p-2">
                    <div class="scout-filter-container d-flex align-items-center">
                        <div class="scout-filter-wrapper">
                            <select 
                                ref="scoutFilterSelect"
                                v-model="scoutFilter" 
                                class="scout-filter-dropdown"
                                @change="updateArrowPosition"
                            >
                                <option value="all">すべて</option>
                                <option value="active">スカウト希望中</option>
                                <option value="expired">スカウト希望期間終了</option>
                            </select>
                            <i 
                                class="fas fa-chevron-down scout-filter-arrow"
                                :style="{ left: arrowPosition + 'px' }"
                            ></i>
                        </div>
                        <div class="scout-status-text">
                            あなたがスカウト希望している求人{{ activeScoutedJobs.length }}件、<span class="remaining-count">残り{{ 10 - activeScoutedJobs.length }}件可能</span>
                        </div>
                    </div>
                </div>
                <div class="section-content">
                    <JobListItem
                        v-for="(job, index) in filteredScoutedJobs"
                        :key="`scouted-${job._id}`"
                        :job="job"
                        :isJobScouted="true"
                        :isJobScoutActive="isJobScoutActive(job)"
                        :index="index"
                        :searchHighlights="job.highlights || []"
                        @click="getJobsToScout(job, index, 'scouted')"
                    />
                </div>
            </div>



            <!-- Other Jobs Section -->
            <Loader :field="'other-jobs'" v-if="listLoading" />
            <div v-else class="job-section">
                <div class="section-content">

                    <div 
                        class="search-row mb-4" 
                        :class="{ 'search-disabled': localShowMatchedJobs }"
                    >
                        <div class="search-input-wrapper">
                            <i class="fa fa-search search-icon"></i>
                            <input
                                type="text"
                                class="search-input"
                                v-model="localSearchKeyword"
                                placeholder="職種、キーワード、企業名"
                                :disabled="localShowMatchedJobs"
                                @keypress.enter="handleSearch"
                            />
                        </div>
                        <button 
                            class="search-button" 
                            @click="handleSearch"
                            :disabled="localShowMatchedJobs"
                        >
                            求人検索
                        </button>
                        <div 
                            v-if="localShowMatchedJobs" 
                            class="search-tooltip"
                        >
                            AIによる求人表示をONにすると求人検索はできません
                        </div>
                    </div>
                    <!-- Toggle component -->
                    <div class="toggle-container p-2 mb-2">
                        <span class="toggle-label ml-2">AIによるマッチ求人のみ表示</span>
                        <toggle-button
                            v-model="localShowMatchedJobs"
                            :sync="true"
                            :labels="false"
                            :width="50"
                            :height="24"
                            color="#2F80ED"
                        />
                    </div>

                    <!-- Dropdown filter -->
                    <div class="company-filter-container mb-4">
                        <div class="company-filter-wrapper">
                            <select 
                                ref="companyFilterSelect"
                                v-model="companyFilter" 
                                @change="handleCompanyFilterChange" 
                                class="company-filter-dropdown"
                            >
                                <option value="all">すべて</option>
                                <option value="registered">あなたがキャリア登録している企業</option>
                                <option value="not-registered">キャリア登録していない企業</option>
                            </select>
                            <i 
                                class="fas fa-chevron-down company-filter-arrow"
                                :style="{ left: companyArrowPosition + 'px' }"
                            ></i>
                        </div>
                    </div>

                    <JobListItem
                        v-for="(job, index) in displayedOtherJobs"
                        :key="`other-${job._id}`"
                        :job="job"
                        :isJobScouted="isJobScoutedById(job._id)"
                        :index="index"
                        :searchHighlights="job.highlights || []"
                        @click="getJobsToScout(job, index, 'other')"
                    />
                    
                    <!-- Pagination -->
                    <div class="pagination-area" v-if="totalPages > 1 && !showMatchedJobs && otherJobs.length > 0">
                        <Pagination
                            :total-page="totalPages"
                            :callback="handlePageChange"
                            :currentPage="currentPage"
                        />
                    </div>
                </div>
            </div>
        </div>

        <CompanySelectionModal
            ref="companySelectionModal"
            :companyName="selectedJobCompany"
            :companyId="selectedJob.company_id"
            :companyDescription="selectedCompany && selectedCompany.short_description"
            :loading="loading"
            @proceed="handleCompanySelectionProceed"
            @cancel="handleCompanySelectionCancel"
        />

        <TermsConditionsModal
            ref="termsConditionsModal"
            @agree="handleTermsAgree"
            @cancel="handleTermsCancel"
        />

        <JobDescriptionModal
            ref="jobModal"
            :currentJob="selectedJob"
            :currentIndex="actualJobIndex"
            :totalJobs="totalJobsCount"
            :isJobScouted="isSelectedJobScouted"
            :hasReachedScoutLimit="hasReachedScoutLimit"
            :hasCompanyActiveScout="hasCompanyActiveScout"
            :loading="loadingJobDetails"
            :company="profile.company_name"
            @scout="openScoutConfirmation"
            @prev="handlePrev"
            @next="handleNext"
        />
        <ScoutConfirmationModal
            ref="scoutConfirmModal"
            @confirm="checkSharedProfile"
            @cancel="handleScoutCancel"
            :loading="loading"
        />

        <!-- Success Modal after scout submission -->
        <BaseModal
            v-if="showScoutSuccess"
            ref="scoutSuccessModal"
            :modal-id="'scoutSuccessModal'"
            :modal-text="'スカウト希望が完了しました！'"
            :button-text="'OK'"
            @button-click="handleScoutSuccessOk"
        />
    </div>
</template>

<script>
import JobDescriptionModal from "./JobDescriptionModal.vue";
import ScoutConfirmationModal from "./ScoutConfirmationModal.vue";
import CompanySelectionModal from "./CompanySelectionModal.vue";
import TermsConditionsModal from "./TermsConditionsModal.vue";
import BaseModal from "./BaseModal.vue";
import JobListItem from "./JobListItem.vue";
import Loader from "../Loader.vue";
import Api from "../../config/api";
import Pagination from "../Pagination.vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
export default {
    name: "JobLists",
    components: {
        JobDescriptionModal,
        ScoutConfirmationModal,
        CompanySelectionModal,
        TermsConditionsModal,
        BaseModal,
        JobListItem,
        Loader,
        Pagination,
    },
    props: {
        showMatchedJobs: {
            type: Boolean,
            required: true,
        },
        searchKeyword: {
            type: String,
            default: "",
        },
        companies: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            filteredJobs: [],
            jobs: [],
            scoutedJobs: [],
            allJobs: [],
            loading: false,
            loadingScoutedJobs: false,
            selectedJob: {},
            jobsByCompany: [], // Jobs from the same company
            currentJobIndex: 0,
            loadingJobDetails: false,
            showScoutConfirmation: false,
            clientPage: 1,
            pageSize: 20,
            scoutedJobsCollapsed: false,
            otherJobsCollapsed: false,
            selectedJobType: 'other', // 'scouted' or 'other'
            showScoutSuccess: false,
            scoutFilter: 'all', // 'all', 'active', 'expired'
            companyFilter: 'all', // 'all', 'registered', 'not-registered'
            arrowPosition: 76, // Default arrow position
            companyArrowPosition: 76, // Default company arrow position
        };
    },
    computed: {
        ...mapGetters({
            profile: 'getProfile',
            serverJobs: 'jobs/getJobs',
            totalJobs: 'jobs/getTotalJobs',
            currentPage: 'jobs/getCurrentPage',
            totalPages: 'jobs/getTotalPages',
            listLoading: 'jobs/getLoading'
        }),
        // Add computed property for two-way binding
        localShowMatchedJobs: {
            get() {
                return this.showMatchedJobs;
            },
            set(value) {
                this.$emit('toggle-change', value);
            }
        },
        selectedJobCompany() {
            return this.selectedJob && this.selectedJob.companyName 
        },
        selectedCompany() {
            const companyId = this.selectedJob && this.selectedJob.company_id;
            if (!companyId || !this.companies || !this.companies.length) return null;
            return this.companies.find(c => c._id === companyId) || null;
        },
        otherJobs() {
            if (this.showMatchedJobs) {
                // Filter out scouted jobs from the main jobs list
                const scoutedJobIds = this.scoutedJobs.map(job => job._id);
                return this.filteredJobs.filter(job => !scoutedJobIds.includes(job._id));
            }
            return this.serverJobs;
        },
        displayedOtherJobs() {
            let jobs = [];
            if (this.showMatchedJobs) {
                const start = (this.clientPage - 1) * this.pageSize;
                const end = start + this.pageSize;
                jobs = this.otherJobs.slice(start, end);
            } else {
                jobs = this.otherJobs;
            }
            
            // Merge scouted job data (including scoutedAt) if job is scouted
            return jobs.map(job => {
                const scoutedJob = this.scoutedJobs.find(sj => sj._id === job._id);
                if (scoutedJob) {
                    return {
                        ...job,
                        scoutedAt: scoutedJob.scoutedAt,
                        status: scoutedJob.status,
                        expiresAt: scoutedJob.expiresAt
                    };
                }
                return job;
            });
        },
        totalJobsCount() {
            if (this.selectedJobType === 'scouted') {
                return this.filteredScoutedJobs.length;
            }
            return this.showMatchedJobs ? this.otherJobs.length : this.totalJobs;
        },
        actualJobIndex() {
            if (this.selectedJobType === 'scouted') {
                return this.currentJobIndex;
            }
            if (this.showMatchedJobs) {
                return this.currentJobIndex;
            }
            // Calculate actual index based on current page (20 items per page)
            return ((this.currentPage - 1) * 20) + this.currentJobIndex;
        },
        isCurrentJobScouted() {
            return this.selectedJobType === 'scouted';
        },
        isSelectedJobScouted() {
            const id = this.selectedJob && this.selectedJob._id;
            if (!id) return false;
            return this.scoutedJobs.some(j => j._id === id);
        },
        activeScoutedJobs() {
            console.log(this.scoutedJobs.filter(job => job.status === 'active'));
            return this.scoutedJobs.filter(job => job.status === 'active');
        },
        filteredScoutedJobs() {
            if (this.scoutFilter === 'all') {
                return this.scoutedJobs;
            } else if (this.scoutFilter === 'active') {
                return this.scoutedJobs.filter(job => job.status === 'active');
            } else if (this.scoutFilter === 'expired') {
                return this.scoutedJobs.filter(job => job.status === 'expired');
            }
            return this.scoutedJobs;
        },
        hasReachedScoutLimit() {
            if (this.activeScoutedJobs.length >= 10) return true;
            const companyId = this.selectedJob && (this.selectedJob.company_id || this.selectedJob.companyId);
            if (!companyId) return false;
            return this.activeScoutedJobs.some(j => (j.companyId || j.company_id) && String(j.companyId || j.company_id) === String(companyId));
        },
        hasCompanyActiveScout() {
            // Check if there's an active scout from the same company as the selected job
            const companyId = this.selectedJob && (this.selectedJob.company_id || this.selectedJob.companyId);
            if (!companyId || !this.activeScoutedJobs || !this.activeScoutedJobs.length) return false;
            return this.activeScoutedJobs.some(j => 
                (j.companyId || j.company_id) && 
                String(j.companyId || j.company_id) === String(companyId)
            );
        },
    },
    methods: {
        ...mapActions({
            loadJobs: 'jobs/loadJobs'
        }),
        ...mapMutations({
            setPage: 'setPage'
        }),
        updateArrowPosition() {
            this.$nextTick(() => {
                if (this.$refs.scoutFilterSelect) {
                    const select = this.$refs.scoutFilterSelect;
                    const selectedOption = select.options[select.selectedIndex];
                    const text = selectedOption.text;
                    
                    // Create a temporary span to measure text width
                    const tempSpan = document.createElement('span');
                    tempSpan.style.visibility = 'hidden';
                    tempSpan.style.position = 'absolute';
                    tempSpan.style.fontSize = '14px';
                    tempSpan.style.fontFamily = window.getComputedStyle(select).fontFamily;
                    tempSpan.textContent = text;
                    document.body.appendChild(tempSpan);
                    
                    const textWidth = tempSpan.offsetWidth;
                    document.body.removeChild(tempSpan);
                    
                    // Position arrow after text with 8px spacing, plus left padding (18px)
                    this.arrowPosition = 18 + textWidth + 8;
                }
            });
        },
        updateCompanyArrowPosition() {
            this.$nextTick(() => {
                if (this.$refs.companyFilterSelect) {
                    const select = this.$refs.companyFilterSelect;
                    const selectedOption = select.options[select.selectedIndex];
                    const text = selectedOption.text;
                    
                    // Create a temporary span to measure text width
                    const tempSpan = document.createElement('span');
                    tempSpan.style.visibility = 'hidden';
                    tempSpan.style.position = 'absolute';
                    tempSpan.style.fontSize = '14px';
                    tempSpan.style.fontFamily = window.getComputedStyle(select).fontFamily;
                    tempSpan.textContent = text;
                    document.body.appendChild(tempSpan);
                    
                    const textWidth = tempSpan.offsetWidth;
                    console.log(textWidth)
                    document.body.removeChild(tempSpan);
                    
                    // Position arrow after text with 8px spacing, plus left padding (18px)
                    this.companyArrowPosition = 18 + textWidth + 8;
                }
            });
        },
        async handleCompanyFilterChange() {
            // Reload jobs with the new company filter
            if (!this.showMatchedJobs) {
                this.setPage(1);
                await this.loadJobs({ 
                    page: 1, 
                    searchKeyword: this.localSearchKeyword,
                    companyFilter: this.companyFilter
                });
                // Recalculate arrow position after jobs load (DOM might have changed)
                this.$nextTick(() => {
                    this.updateCompanyArrowPosition();
                });
            } else {
                // If matched jobs are shown, filter user-based jobs client-side
                const shouldIncludeJob = (job) => {
                    const jobCompanyId = job.company_id || job.companyId;
                    if (!jobCompanyId) return false;
                    
                    const isSubscribed = this.profile.other_companies.includes(String(jobCompanyId));
                    
                    if (this.companyFilter === 'registered') {
                        return isSubscribed;
                    } else if (this.companyFilter === 'not-registered') {
                        return !isSubscribed;
                    }
                    return true; // 'all' or any other filter
                };
                
                this.filteredJobs = this.jobs.filter(shouldIncludeJob);
                
                // Update arrow position after filtering
                this.$nextTick(() => {
                    this.updateCompanyArrowPosition();
                });
            }
        },
        async handleSearch() {
            if (this.showMatchedJobs) {
                // Search is disabled when matched jobs are active
                return;
            }
            // Perform server-side search
            await this.performServerSearch();
        },
        performClientSearch() {
            if (!this.localSearchKeyword.trim()) {
                // If search is empty, show all jobs
                this.filteredJobs = this.jobs;
                return;
            }
            const searchTerms = this.localSearchKeyword
                .toLowerCase()
                .split(/[,\s]+/)
                .filter((term) => term);

            // Filter jobs that match ALL search terms
            this.filteredJobs = this.jobs.filter((job) => {
                // Create a single searchable text from relevant job fields
                const searchableText = [
                    job.job_title,
                    job.job_description,
                    job.location,
                ]
                    .join(" ")
                    .toLowerCase();

                // Job must match ALL search terms to be included
                return searchTerms.every((term) =>
                    searchableText.includes(term)
                );
            });
        },
        async handlePageChange(page) {
            this.scrollToTop();
            if (this.showMatchedJobs) {
                this.clientPage = page;
            } else {
                this.setPage(page);
                await this.loadJobs({ 
                    page, 
                    searchKeyword: this.localSearchKeyword,
                    companyFilter: this.companyFilter
                });
            }
        },
        async performServerSearch() {
            await this.loadJobs({ 
                page: 1, 
                searchKeyword: this.localSearchKeyword,
                companyFilter: this.companyFilter
            });
        },
        getJobsToScout(job, index, jobType) {
            // Find the index of the clicked job in the appropriate list
            this.currentJobIndex = index;
            this.selectedJobType = jobType;
            if (this.currentJobIndex === -1) this.currentJobIndex = 0;

            // Set the selected job
            this.selectedJob = job;

            // Show the modal
            this.$refs.jobModal.show();
        },
        async handlePrev() {
            let currentJobs = this.selectedJobType === 'scouted' ? this.scoutedJobs : this.displayedOtherJobs;
            
            // If there are previous jobs in current section, move to previous
            if (this.currentJobIndex > 0) {
                this.currentJobIndex = this.currentJobIndex - 1;
                this.selectedJob = currentJobs[this.currentJobIndex];
                return;
            }
            
            // Handle beginning of current section based on job type
            switch (this.selectedJobType) {
                case 'scouted':
                    console.log('first scouted job - closing modal');
                    this.$refs.jobModal.hide();
                    break;
                    
                case 'other':
                    if (!this.showMatchedJobs && this.currentPage > 1) {
                        // Load previous page for paginated jobs
                        const prevPage = this.currentPage - 1;
                        this.setPage(prevPage);
                        await this.loadJobs({ 
                            page: prevPage, 
                            searchKeyword: this.localSearchKeyword,
                            companyFilter: this.companyFilter
                        });
                        // Set index to last job of the previous page
                        this.currentJobIndex = this.displayedOtherJobs.length - 1;
                        this.selectedJob = this.displayedOtherJobs[this.currentJobIndex];
                        this.scrollToTop();
                    } else {
                        // Close modal for matched jobs or when no previous pages
                        console.log('closing modal - no previous other jobs');
                        this.$refs.jobModal.hide();
                    }
                    break;
                    
                default:
                    console.log('closing modal - unknown job type');
                    this.$refs.jobModal.hide();
            }
        },
        async handleNext() {
            let currentJobs = this.selectedJobType === 'scouted' ? this.scoutedJobs : this.displayedOtherJobs;
            
            
            // If there are more jobs in current section, move to next
            if (this.currentJobIndex < currentJobs.length - 1) {
                this.currentJobIndex = this.currentJobIndex + 1;
                this.selectedJob = currentJobs[this.currentJobIndex];
                return;
            }
            
            // Handle end of current section based on job type
            switch (this.selectedJobType) {
                case 'scouted':
                    console.log('last scouted job - closing modal');
                    this.$refs.jobModal.hide();
                    break;
                    
                case 'other':
                    if (!this.showMatchedJobs && this.currentPage < this.totalPages) {
                        // Load next page for paginated jobs;
                        const nextPage = this.currentPage + 1;
                        this.setPage(nextPage);
                        await this.loadJobs({ 
                            page: nextPage, 
                            searchKeyword: this.localSearchKeyword,
                            companyFilter: this.companyFilter
                        });
                        this.currentJobIndex = 0;
                        this.selectedJob = this.displayedOtherJobs[this.currentJobIndex];
                        this.scrollToTop();
                    } else {
                        // Close modal for matched jobs or when no more pages
                        console.log('closing modal - no more other jobs');
                        this.$refs.jobModal.hide();
                    }
                    break;
                    
                default:
                    console.log('closing modal - unknown job type');
                    this.$refs.jobModal.hide();
            }
        },
        openScoutConfirmation() {
            this.showScoutConfirmation = true;
            this.$nextTick(() => {
                this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.show();
            });
        },
        handleScoutCancel() {
            this.showScoutConfirmation = false;
            this.$nextTick(() => {
                this.$refs.scoutConfirmModal.hide();
            });
        },
        handleCompanySelectionProceed() {
            // Proceed to terms modal
            if (this.$refs.termsConditionsModal) {
                this.$refs.termsConditionsModal.show();
            }
        },
        handleCompanySelectionCancel() {
            if (this.$refs.companySelectionModal) {
                this.$refs.companySelectionModal.hide();
            }
        },
        handleTermsAgree() {
            // Submit and close
            this.submitScoutRequest(this.selectedJob, this.selectedJob.company_id);
            if (this.$refs.termsConditionsModal) {
                this.$refs.termsConditionsModal.hide();
            }
        },
        handleTermsCancel() {
            if (this.$refs.termsConditionsModal) {
                this.$refs.termsConditionsModal.hide();
            }
        },
        checkSharedProfile(){
            const companyId = this.selectedJob && this.selectedJob.company_id;
            const hasSharedProfile = this.profile && this.profile.other_companies && this.profile.other_companies.includes(companyId);
            if (!hasSharedProfile) {
                this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.hide();
                this.showScoutConfirmation = false;
                if (this.$refs.companySelectionModal) {
                    this.$refs.companySelectionModal.show();
                }
            } else {
                this.handleScout();
            }
        },
        async handleScout() {
            try {
                this.loading = true;
                await this.submitScoutRequest(this.selectedJob);
                this.$nextTick(() => {
                    // this.$refs.scoutConfirmModal.hide();

                    this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.hide();
                    this.showScoutConfirmation = false;
                    // this.handleNext();
                });
            } catch (error) {
                console.error("Scout request failed:", error);
                // Handle error (show error message, etc.)
            } finally {
                this.loading = false;
            }
        },
        async submitScoutRequest(jobId, companyToShare=null) {
            // Implement your API call here
            try {
                const token = await this.$auth.getToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                const { _id, company_id } = jobId;
                const res = await this.axios({
                    method: "post",
                    url: Api.url(Api.endpoints.addToScout),
                    headers,
                    data: {
                        jobId: _id,
                        jobCompany: company_id,
                        companyToShare,
                    },
                });
                const scoutedJob = this.displayedOtherJobs.find(
                    (job) => job._id === jobId
                );
                const jobToPush = scoutedJob || this.selectedJob || null;
                if (
                    jobToPush &&
                    !this.scoutedJobs.some((job) => job._id === jobId)
                ) {
                    // Add scoutedAt from the API response
                    const jobWithScoutDate = {
                        ...jobToPush,
                        scoutedAt: res.data.data.scoutedAt,
                        status: res.data.data.status || 'active'
                    };
                    this.scoutedJobs.unshift(jobWithScoutDate);
                }
                // Show success modal
                this.$refs.companySelectionModal && this.$refs.companySelectionModal.hide();
                this.showScoutSuccess = true;
            } catch (err) {
                console.log("Submitting scout request for job:", jobId);
            }
        },
        isJobScoutActive(job) {
            const scoutedJob = this.scoutedJobs.find(j => j._id === job._id);
            if (!scoutedJob) return false;
            // If job is active, it's scouted; otherwise return false (not scouted)
            return scoutedJob.status === 'active';
        },
        isJobScoutedById(jobId) {
            return this.scoutedJobs.some(job => job._id === jobId);
        },
        handleScoutSuccessOk() {
            // Close success modal
            this.showScoutSuccess = false;
        },
        async getUserBasedJobs() {
            try {
                this.loadingScoutedJobs = true;
                const token = await this.$auth.getToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                const jobs = await this.axios({
                    method: "get",
                    url: Api.url(Api.endpoints.allJobs),
                    headers,
                });
                //   console.log(jobs);
                this.filteredJobs = jobs.data;
                this.jobs = jobs.data;
            } catch (err) {
                console.error("Error fetching jobs:", err);
            } finally {
                this.loadingScoutedJobs = false;
            }
        },
        async getJobseekerScoutedJobs() {
            try {
                this.loadingScoutedJobs = true;
                const token = await this.$auth.getToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                const jobs = await this.axios({
                    method: "get",
                    url: Api.url(Api.endpoints.scoutedJobs),
                    headers,
                });
                this.scoutedJobs = jobs.data;
                // console.log(this.scoutedJobs);
            } catch (err) {
                console.error("Error fetching jobs:", err);
            } finally {
                this.loadingScoutedJobs = false;
            }
        },
        scrollToTop() {
            window.scrollTo(0, 0);
        },
    },
    async created() {
        await this.getUserBasedJobs();
        await this.getJobseekerScoutedJobs();
        // Only load jobs initially if toggle is ON (showMatchedJobs = true)
        // When toggle is OFF, jobs will only load when user searches
        if (this.showMatchedJobs) {
            // Matched jobs are already loaded via getUserBasedJobs
        }
    },
    mounted() {
        this.updateArrowPosition();
        this.updateCompanyArrowPosition();
    },
    watch: {
        showMatchedJobs: {
            handler(newVal) {
                // When toggle is turned OFF, don't load jobs automatically
                // Jobs will only load when user performs a search
                // if (!newVal) {
                //     this.performServerSearch();
                // }
            },
        },
        // Keep localSearchKeyword in sync with prop and notify parent on changes
        searchKeyword: {
            immediate: true,
            handler(newVal) {
                this.localSearchKeyword = newVal || '';
            }
        },
        localSearchKeyword(newVal) {
            if (newVal !== this.searchKeyword) {
                this.$emit('search', newVal);
            }
        }
    },
};
</script>

<style scoped>
.jobs {
    justify-content: space-between;
}
.job-list {
    display: flex;
    flex-direction: column;
}

.job-section {
    margin-bottom: 20px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #ffffff;
    /* border: 1px solid #e9ecef; */
    /* border-radius: 4px; */
    transition: background-color 0.2s ease;
}


.header-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.section-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #000000;
}

.collapse-icon {
    font-size: 14px;
    color: black;
    transition: transform 0.2s ease;
}

.section-content {
    margin-top: 8px;
}

/* .job-card {
    background-color: white;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    padding: 5px 50px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    height: 60px;
}

.job-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
} */

/* .company-name {
    font-weight: bold;
    font-size: 16px;
    color: #3c3c3c;
    font-weight: 600;
    margin-bottom: 5px;
} */

.job-title {
    font-size: 14px;
    font-weight: 600;
    color: #3c3c3c;
}
.scouted-badge {
    margin: auto 0px;
}

.scouted-badge-text {
    width: 60px;
    border: 1px solid #fea403;
    color: #fea403;
    text-align: center;
    font-weight: bold;
    font-size: 10px;
}

/* Toggle container styles */
.toggle-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #ffffff;
    font-size: 12px;
    margin: auto;
    width: 50%;
    border-radius: 8px;
}

.toggle-label {
    margin-right: 10px;
}

.search-row {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    background-color: #ffffff;
    border: 1px solid #ccc;
    border-radius: 10px;
    height: 87px;
    padding: 0 12px;
    margin: auto;
    width: 50%;
    position: relative;
}

.search-label {
    margin-right: 15px;
    min-width: 80px;
    white-space: nowrap;
    font-size: 12px;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    margin-right: 10px;
    flex: 1;
    height: 100%;
}

.search-icon {
    position: absolute;
    left: 12px;
    color: #666;
    font-size: 20px;
    pointer-events: none;
    z-index: 1;
}

.search-input {
    padding: 0 12px 0 45px;
    border: none;
    border-radius: 0;
    background-color: transparent;
    font-size: 20px;
    width: 100%;
    height: 100%;
    outline: none;
}

.search-input::placeholder {
    font-size: 20px;
    color: #666;
}

.search-button {
    padding: 8px 12px;
    background-color: #4F46E5;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 20px;
    color: #fff;
    height: 60px;
    min-width: 133px;
    font-weight: bold;
}

.search-button:disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.search-disabled {
    opacity: 0.85;
    cursor: not-allowed;
}

.search-input:disabled {
    cursor: not-allowed;
}

.search-tooltip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    pointer-events: none;
    opacity: 0;
    transition: none;
}

.search-row:hover .search-tooltip {
    opacity: 1;
}

.search-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #333;
}

.scouted-header {
    border-bottom: 1px solid #e9ecef;
}

.scout-filter-container {
    width: 100%;
}

.scout-filter-wrapper {
    position: relative;
    display: inline-block;
    min-width: 268px;
}

.scout-filter-dropdown {
    padding: 8px 40px 8px 18px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #F9FAFB;
    font-size: 14px;
    color: #3c3c3c;
    cursor: pointer;
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: none;
}

.scout-filter-dropdown:focus {
    outline: none;
    border-color: #75C791;
}

.scout-filter-arrow {
    position: absolute;
    right: auto;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 18px;
    color: #3c3c3c;
}

.scout-filter-dropdown option {
    padding-left: 18px;
}

@media (max-width: 767px) {
    .scout-filter-container {
        flex-direction: column;
        align-items: flex-start !important;
    }
    
    .scout-status-text {
        margin-left: 0;
        margin-top: 8px;
        order: -1;
    }
    
    .scout-filter-wrapper {
        min-width: 102px;
        width: 100%;
    }
    
    .scout-filter-dropdown {
        min-width: 102px;
    }
    
    .scout-filter-arrow {
        font-size: 16px;
    } 
    .toggle-container {
        width: 100%;
    }
    
    .search-row {
        height: 60px;
        width: 100% !important;
    }
    
    .search-icon {
        font-size: 14px;
    }
    
    .search-input {
        font-size: 14px;
        padding: 0 12px 0 35px;
    }
    
    .search-input::placeholder {
        font-size: 14px;
    }
    
    .search-button {
        width: 100px;
        height: 40px;
        font-size: 14px;
        min-width: 100px;
    }
}

.scout-status-text {
    font-size: 12px;
    color: #3c3c3c;
    margin-left: 12px;
}

.scout-status {
    font-size: 12px;
    color: #3c3c3c;
    margin: 8px 16px 8px 16px;
    padding: 0;
}

.remaining-count {
    color: #ff0000;
    font-weight: bold;
}

.company-filter-container {
    width: 296px;
    padding: 4px 0px;
    height: 38px;
}

.company-filter-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
}

.company-filter-dropdown {
    padding: 8px 40px 8px 18px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #F9FAFB;
    font-size: 14px;
    color: #3c3c3c;
    cursor: pointer;
    min-width: 180px;
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: none;
}

.company-filter-dropdown:focus {
    outline: none;
    border-color: #75C791;
}

.company-filter-arrow {
    position: absolute;
    right: auto;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 18px;
    color: #3c3c3c;
}

.company-filter-dropdown option {
    padding-left: 18px;
}

@media (max-width: 767px) {
    .company-filter-container {
        width: 100%;
    }
    .company-filter-wrapper {
        min-width: 102px;
        width: 100%;
    }
}
</style>
